import { useState, useEffect } from "react";
import {
  Building2, ChevronRight, ChevronDown, ToggleRight, ToggleLeft, X, RefreshCw, MessageSquare
} from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT, useDeptName, useLang } from "@/contexts/LanguageContext";
import { DEPT_ICONS } from "@/utils/constants";
import EnergyCO2Summary from "@/components/views/EnergyCO2Summary";

const DepartmentView = ({ clientId, department, onBack, auditType }) => {
  const t = useT();
  const dn = useDeptName();
  const lang = useLang();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState({});
  const [observations, setObservations] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({});

  const DeptIcon = DEPT_ICONS[department.key] || Building2;
  const baseUrl = auditType === "residential"
    ? `${API}/clients/${clientId}/residential/departments/${department.key}`
    : `${API}/clients/${clientId}/departments/${department.key}`;

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const response = await axios.get(`${baseUrl}/audit?lang=${lang}`);
        setAudit(response.data);
        const initialAnswers = {};
        response.data.questions?.forEach((q) => {
          initialAnswers[q.question_id] = q.answer || "";
        });
        setAnswers(initialAnswers);
        setObservations(response.data.observations || "");
      } catch (error) {
        console.error("Error fetching audit:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
  }, [baseUrl, lang]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedQuestions = audit.questions.map((q) => ({
        ...q,
        answer: answers[q.question_id] || null
      }));
      const response = await axios.put(`${baseUrl}/audit`, {
        questions: updatedQuestions,
        observations: observations
      });
      setAudit(response.data);
    } catch (error) {
      console.error("Error saving audit:", error);
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const answeredCount = Object.values(answers).filter((a) => a !== "" && a !== null && a !== undefined).length;
  const totalCount = audit?.questions?.length || 0;

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  const sections = audit?.sections || [];
  const groupedQuestions = {};
  audit?.questions?.forEach((q) => {
    const secKey = q.section || "_unsorted";
    const subKey = q.subsection || "_unsorted";
    if (!groupedQuestions[secKey]) groupedQuestions[secKey] = {};
    if (!groupedQuestions[secKey][subKey]) groupedQuestions[secKey][subKey] = [];
    groupedQuestions[secKey][subKey].push(q);
  });

  const renderInput = (question) => {
    const qid = question.question_id;
    const val = answers[qid] || "";
    const rt = question.response_type || "text";

    if (rt === "boolean") {
      const isYes = val === "Yes" || val === "true" || val === true;
      return (
        <div className="boolean-toggle" data-testid={`input-${qid}`}>
          <button
            type="button"
            className={`toggle-btn ${isYes ? "active yes" : ""}`}
            onClick={() => setAnswers({ ...answers, [qid]: "Yes" })}
          >
            <ToggleRight size={16} /> Yes
          </button>
          <button
            type="button"
            className={`toggle-btn ${val === "No" ? "active no" : ""}`}
            onClick={() => setAnswers({ ...answers, [qid]: "No" })}
          >
            <ToggleLeft size={16} /> No
          </button>
          {val && (
            <button
              type="button"
              className="toggle-btn clear"
              onClick={() => setAnswers({ ...answers, [qid]: "" })}
            >
              <X size={14} />
            </button>
          )}
        </div>
      );
    }

    if (rt === "select" && question.options) {
      return (
        <select
          className="form-select"
          value={val}
          onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
          data-testid={`input-${qid}`}
        >
          <option value="">-- Select --</option>
          {question.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (rt === "numeric") {
      return (
        <div className="numeric-input-wrapper">
          <input
            type="number"
            step="any"
            className="form-input"
            placeholder="Enter value..."
            value={val}
            onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
            data-testid={`input-${qid}`}
          />
          {question.unit && <span className="input-unit-badge">{question.unit}</span>}
        </div>
      );
    }

    return (
      <textarea
        className="form-textarea"
        placeholder="Enter your answer..."
        value={val}
        onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
        data-testid={`input-${qid}`}
      />
    );
  };

  let questionCounter = 0;

  return (
    <div data-testid="department-view">
      <div className="page-header flex-between">
        <div className="flex gap-md" style={{ alignItems: "center" }}>
          <button className="btn btn-secondary" onClick={onBack}>{t("back")}</button>
          <div className="department-card-icon" style={{ width: "48px", height: "48px" }}><DeptIcon size={24} /></div>
          <div>
            <h2 className="page-title" style={{ marginBottom: 0 }}>{dn(department.key)} - {t("audit")}</h2>
            <span className={`badge badge-${department.status === "completed" ? "success" : department.status === "in_progress" ? "warning" : "neutral"}`}>
              {department.status === "completed" ? t("completed") : department.status === "in_progress" ? t("in_progress") : t("not_started")}
            </span>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="save-audit-btn">
          {saving ? t("saving") : t("save_all_answers")}
        </button>
      </div>

      <div className="audit-progress-bar-container">
        <div className="flex-between mb-sm">
          <span className="text-muted">{answeredCount} {t("of")} {totalCount} {t("questions_answered")}</span>
          <span className="text-muted">{totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0}%</span>
        </div>
        <div className="progress-bar">
          <div className={`progress-fill ${answeredCount === totalCount && totalCount > 0 ? "success" : ""}`} style={{ width: `${totalCount > 0 ? (answeredCount / totalCount) * 100 : 0}%` }}></div>
        </div>
      </div>

      {sections.length > 0 ? (
        sections.map((section) => {
          const sectionQuestions = groupedQuestions[section.id] || {};
          const sectionQCount = Object.values(sectionQuestions).flat().length;
          const sectionAnswered = Object.values(sectionQuestions).flat().filter((q) => {
            const v = answers[q.question_id];
            return v !== "" && v !== null && v !== undefined;
          }).length;
          const isCollapsed = collapsedSections[section.id];

          return (
            <div className="audit-section" key={section.id} data-testid={`section-${section.id}`}>
              <div className="audit-section-header" onClick={() => toggleSection(section.id)}>
                <div className="audit-section-title">
                  {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                  <h3>{section.name}</h3>
                  <span className="badge badge-neutral">{sectionAnswered}/{sectionQCount}</span>
                </div>
              </div>
              {!isCollapsed && (
                <div className="audit-section-body">
                  {(section.subsections || []).map((sub) => {
                    const subQuestions = sectionQuestions[sub.id] || [];
                    if (subQuestions.length === 0) return null;
                    return (
                      <div className="audit-subsection" key={sub.id} data-testid={`subsection-${sub.id}`}>
                        <h4 className="audit-subsection-title">{sub.name}</h4>
                        <div className="questions-list">
                          {subQuestions.map((question) => {
                            questionCounter++;
                            return (
                              <div className="question-item" key={question.question_id} data-testid={`question-${question.question_id}`}>
                                <div className="question-header">
                                  <span className="question-number">Q{questionCounter}</span>
                                  <span className="question-text">{question.question}</span>
                                  {question.emission_factor !== 0 && question.emission_factor != null && (
                                    <span className={`emission-badge ${question.emission_factor < 0 ? "negative" : "positive"}`} title="Emission Factor">
                                      {question.emission_factor > 0 ? "+" : ""}{question.emission_factor} kg CO2e
                                    </span>
                                  )}
                                  {question.is_dynamic && (
                                    <span className="dynamic-badge" title="Dynamic data - updated regularly">
                                      <RefreshCw size={12} /> Dynamic
                                    </span>
                                  )}
                                </div>
                                <div className="question-input">
                                  {renderInput(question)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div className="card">
          {audit?.questions?.map((question, index) => (
            <div className="question-item" key={question.question_id}>
              <div className="question-header">
                <span className="question-number">Q{index + 1}</span>
                <span className="question-text">{question.question}</span>
              </div>
              <div className="question-input">
                {renderInput(question)}
              </div>
            </div>
          ))}
        </div>
      )}

      <EnergyCO2Summary questions={audit?.questions || []} answers={answers} sections={audit?.sections || []} />

      <div className="card mt-lg" data-testid="observations-box">
        <h4 className="card-title" style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
          <MessageSquare size={20} /> {t("auditor_observations")}
        </h4>
        <p className="text-muted mb-sm" style={{ fontSize: "var(--text-small)" }}>
          {t("observations_subtitle")}
        </p>
        <textarea
          className="form-textarea"
          rows={6}
          placeholder={t("observations_placeholder")}
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
          data-testid="observations-textarea"
          style={{ minHeight: "120px" }}
        />
      </div>
    </div>
  );
};

export default DepartmentView;
